import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

// Redux
import { resizeParent } from '../../services/Utils'
import { configure, getConfiguration } from '../../actions/Settings'
import { getPushPayGroups } from './redux/pushpay.actions';

// Components
import Spinner from '../Spinner';
import SelectMenu from '../SelectMenu';
import SearchInput from '../SearchInput';
import TwentySeventeenGroupCard from '../Groups/Cards/TwentySeventeenGroupCard';
import EmphasisGroupCard from '../Groups/Cards/EmphasisGroupCard';
import DefaultCard from '../Groups/Cards/DefaultCard';
import TCCGroupsMap from '../Groups/TCCGroupsMap';
import TCCGroupsOpenMap from '../Groups/TCCGroupsOpenMap';

const cleanString = (str = null) => {
    if (!str) return null;
    return str.toLowerCase();
}

export const PushPayGroups = (props) => {

    const dispatch = useDispatch();

    // Filters
    const [type, setType] = useState(props.type);
    const [campus, setCampus] = useState(props.campus);
    const [department, setDepartment] = useState(props.department);
    const [area, setArea] = useState(props.area);
    const [when, setWhen] = useState(props.when);
    const [showMap, setShowMap] = useState((props.map === 'open_map') ? true : false);
    const [loading, setLoading] = useState(true);
    const [searchString, setSearchString] = useState(null);
    const [groups, setGroups] = useState([]);
    const [displayGroups, setDisplayGroups] = useState([]);

    // Options
    const [typeOptions, setTypeOptions] = useState([]);
    const [campusOptions, setCampusOptions] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [areaOptions, setAreaOptions] = useState([]);
    const [whenOptions, setWhenOptions] = useState([]);

    // Labels
    const typeLabel = (props.typelabel) ? props.typelabel : "Type";
    const campusLabel = (props.campuslabel) ? props.campuslabel : "Campus";
    const departmentLabel = (props.departmentlabel) ? props.departmentlabel : "Department";
    const areaLabel = (props.arealabel) ? props.arealabel : "Area";
    const whenLabel = (props.whenlabel) ? props.whenlabel : "When";

    // Defaults
    const limit = (props.limit) ? parseInt(props.limit) : null;
    const showFilters = (props.showfilters === 'false') ? false : true;
    const site = props.site;
    const zoom = (props.zoom) ? parseInt(props.zoom) : 11;

    // Store
    const fullSettings = useSelector(state => state.Settings)
    const fullGroups = useSelector(state => state.Groups);

    const parseResult = (res) => {

        let typeArray = [{ value: '', title: 'All', id: 'all' }];
        let typeKeys = {};
        let campusArray = [{ value: '', title: 'All', id: 'all' }];
        let campusKeys = {};
        let departmentArray = [{ value: '', title: 'All', id: 'all' }];
        let departmentKeys = {};
        let areaArray = [{ value: '', title: 'All', id: 'all' }];
        let areaKeys = {};
        let whenArray = [{ value: '', title: 'All', id: 'all' }];
        let whenKeys = {};

        const gs = Object.keys(res).map((group_id, index) => {

            const item = res[group_id];

            // Type
            if (item.raw.group_type && !typeKeys[cleanString(item.raw.group_type)]) {
                typeKeys[cleanString(item.raw.group_type)] = true;
                typeArray.push({ value: cleanString(item.raw.group_type), title: item.raw.group_type, id: index })
            }

            // Campus
            if (item.raw.campus && !campusKeys[cleanString(item.raw.campus)]) {
                campusKeys[cleanString(item.raw.campus)] = true;
                campusArray.push({ value: cleanString(item.raw.campus), title: item.raw.campus, id: index })
            }

            // Department
            if (item.raw.department && !departmentKeys[cleanString(item.raw.department)]) {
                departmentKeys[cleanString(item.raw.department)] = true;
                departmentArray.push({ value: cleanString(item.raw.department), title: item.raw.department, id: index })
            }

            // Area
            if (item.raw.area && !areaKeys[cleanString(item.raw.area)]) {
                areaKeys[cleanString(item.raw.area)] = true;
                areaArray.push({ value: cleanString(item.raw.area), title: item.raw.area, id: index })
            }

            // When
            if (item.raw.meeting_day && !whenKeys[cleanString(item.raw.meeting_day)]) {
                whenKeys[cleanString(item.raw.meeting_day)] = true;
                whenArray.push({ value: cleanString(item.raw.meeting_day), title: item.raw.meeting_day, id: index })

                // Attempt To Order Days
                const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                const ordered = [];
                days.map(day => {
                    whenArray.map(when => {
                        if (when.value.toLowerCase().indexOf(day) > -1) ordered.push(when);
                    })
                })
                whenArray.map(item => {
                    if (!ordered.includes(item)) ordered.push(item);
                })
                whenArray = [...ordered];

            }

            // Groups
            return item;

        })

        // Set Data
        setTypeOptions(typeArray);
        setCampusOptions(campusArray);
        setDepartmentOptions(departmentArray);
        setAreaOptions(areaArray);
        setWhenOptions(whenArray);
        setGroups(gs);
        setLoading(false);

        // Default Filter
        filterGroups();
    }

    const filterGroups = () => {
        const args = { searchString, type, campus, department, when, area };

        if (fullGroups.ui !== 'success') return;
        let f = {};

        const filteredGroups = Object.keys(fullGroups.groups).map((group_id, index) => {
            const group = fullGroups.groups[group_id];

            // Search String
            let searchable = '';
            searchable += (group.title) ? group.title.toLowerCase() + ' ' : '';
            searchable += (group.content) ? group.content.toLowerCase() + ' ' : '';
            searchable += (group.address && group.address.address) ? group.address.address.toLowerCase() + ' ' : '';
            searchable += (group.area) ? group.area.toLowerCase() + ' ' : '';
            searchable += (group.time) ? group.time.toLowerCase() + ' ' : '';
            searchable += (group.when) ? group.when.toLowerCase() + ' ' : '';
            searchable += (group.raw.group_type) ? group.raw.group_type.toLowerCase() + ' ' : '';
            searchable += (group.raw.department) ? group.raw.department.toLowerCase() + ' ' : '';
            searchable += (group.raw.campus) ? group.raw.campus.toLowerCase() + ' ' : '';

            // Apply Filters
            if (searchString && (searchable.indexOf(searchString.toLowerCase())) < 0) return null;
            if (type && (group.raw.group_type.toLowerCase() != type.toLowerCase())) return null;
            if (campus && (group.raw.campus.toLowerCase() != campus.toLowerCase())) return null;
            if (department && (group.raw.department.toLowerCase() != department.toLowerCase())) return null;
            if (area && (group.raw.area.toLowerCase() != area.toLowerCase())) return null;
            if (when && (group.when.toLowerCase() != when.toLowerCase())) return null;

            f[group_id] = group;

            return group;
        });

        // Sort By Name
        let groupsAsArray = Object.values(f);
        groupsAsArray.sort((a, b) => (a.title > b.title) ? 1 : -1)
        setGroups(groupsAsArray);
        resizeParent();
    }

    // On Mount
    useEffect(() => {

        // Configure
        dispatch(configure(site))

        // Load Configuration
        dispatch(getConfiguration());

        // Resize
        resizeParent();

    }, [])

    // Settings: Ready
    useEffect(() => {
        if (!fullSettings.site) return;
        if (fullGroups.ui === 'loading') return;
        const args = {
            type,
            campus,
            department,
            area,
            when,
            page: 1
        }

        // Load Groups
        if (!fullGroups.groups) dispatch(getPushPayGroups(args))

    }, [fullSettings])


    // Groups: Ready
    useEffect(() => {
        // Guard: Loading
        if (fullGroups.ui !== 'success') return;

        // Parse Result
        parseResult(fullGroups.groups);

    }, [fullGroups])



    // Apply Filters
    useEffect(() => {
        filterGroups();
    }, [searchString, type, campus, department, when, area]);


    const renderCards = () => {

        if (!groups) return (<div></div>);
        let count = 0;

        return groups.map(item => {

            if (limit && count >= limit) return;
            count++;
            return (<CSSTransition key={item.ID} timeout={200} classNames="listtransition"><DefaultCard {...item} key={item.ID} /></CSSTransition>)

        })
    }


    // Loading
    if (loading) return (<Spinner />);

    return (
        <div data-component-type="TCCGroups" className="pushpay-groups-container">

            {/************ NAVIGATION BAR *************/}
            {showFilters && (
                <div className="NavigationBar">

                    <SearchInput name="search" placeholder="Search" onChange={(value) => setSearchString(value)} />
                    {!props.type && (<SelectMenu name="type" title={typeLabel} onChange={(e) => setType(e.target.value)} options={typeOptions} />)}
                    {!props.campus && (<SelectMenu name="campus" title={campusLabel} onChange={(e) => setCampus(e.target.value)} options={campusOptions} />)}
                    {!props.department && (<SelectMenu name="department" title={departmentLabel} onChange={(e) => setDepartment(e.target.value)} options={departmentOptions} />)}
                    {!props.when && (<SelectMenu name="when" title={whenLabel} onChange={(e) => setWhen(e.target.value)} options={whenOptions} />)}
                    {!props.area && (<SelectMenu name="area" title={areaLabel} onChange={(e) => setArea(e.target.value)} options={areaOptions} />)}
                    <button onClick={() => setShowMap(!showMap)}>
                        <span className="icon icon-location"></span>
                    </button>
                </div>
            )}
            <TransitionGroup>
                {showMap && (
                    <CSSTransition key={1} timeout={200} classNames="map-transition">
                        <div className="groups-map">
                            <TCCGroupsOpenMap zoom={zoom} groups={groups} center={fullSettings.address} />
                        </div>
                    </CSSTransition>
                )}
            </TransitionGroup>
            <div className="groups-results">
                <div className="posts-loop">
                    <TransitionGroup>{renderCards()}</TransitionGroup>
                </div>
            </div>
        </div>
    )
}
